import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { useSelector } from 'react-redux';
import { getIssuanceReportApi, exportIssuanceReportApi, uniformErrorMessage } from 'api/services/uniformManagement';
import { itemRender, handlePageChange, defaultPagination, pageSizeOptions } from 'utils/table';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge, getGenderLabel, issuanceStatusOptions } from '../../constants';
import ExcelExportButton from 'components/ExcelExportButton';
import ReportFilterBar, { cleanParams } from '../ReportFilterBar';
import useStickyIdentity from '../../components/useStickyIdentity';
import { UNIFORM_LANE, truncatedText } from '../../components/columns';
import '../issuanceMatrix.css';

const toastOpts = { position: 'top-right', autoClose: 3000, transition: Bounce };

/** The plural spec names are the multi-select (CSV) variants — cost centre,
 *  employee, position and uniform item all take a list now. Gender, size,
 *  warehouse and status stay single: they are small closed vocabularies where
 *  "all of them" is the same as "none of them". */
const FIELDS = ['groupBy', 'dateRange', 'casas', 'employees', 'positions', 'gender', 'items', 'size', 'warehouse', 'status'];

/** The grain first — it decides what every other control is refining — then
 *  CASA, the reporting period and status, because "which stores, over what
 *  window, in what state" is the whole shape of a management report. The
 *  other six are refinements of an answer already on screen, so they sit
 *  behind the disclosure rather than pushing the primary four onto a third
 *  line. */
const PRIMARY_FIELDS = ['groupBy', 'casas', 'dateRange', 'status'];

/** The four grains, coarsest question first. Detail is an explicit option
 *  with an empty value rather than only the select's clear (×): it is a view
 *  in its own right here — the one every roll-up drills into — and a view
 *  users must discover by clearing a control is a view most of them never
 *  find. `cleanParams` drops '' before the request is built, so choosing it
 *  sends no ?group_by= at all, which is exactly what the detail list is. */
const GROUP_BY_OPTIONS = [
  { label: 'Cost center & item', value: 'item' },
  { label: 'Cost center', value: 'cost_center' },
  { label: 'Matrix (item × cost center)', value: 'matrix' },
  { label: 'Detail (one row per issuance)', value: '' },
];
// Falsy in, null out: the Detail option's own value is '', so a bare
// `find` would hand back a "group" for the ungrouped grain.
const groupFor = (value) => (value ? GROUP_BY_OPTIONS.find((o) => o.value === value) || null : null);

/** The tab is called "Issuance (Cost Center-wise)", so it opens on a
 *  cost-centre roll-up rather than on the flat line list it used to show.
 *  Clear restores this, not {}. */
const DEFAULT_FILTERS = { group_by: 'item' };

/** What the grouped grains count, server-side, when the caller names no
 *  status: REJECTED and RETURNED garments came back and SUPERSEDED ones
 *  were retired, so they are not what a store drew. A drill-down sends it
 *  explicitly, so the detail list it lands on describes the same issuances
 *  as the row it came from — and the filter bar then shows it, rather than
 *  leaving the reader to wonder why the numbers moved. */
const GROUPED_STATUSES = 'ISSUED,ACCEPTED';

const TOTAL = 'issuance-matrix-total';
const sumOf = (rows, pick) => rows.reduce((acc, r) => acc + (pick(r) || 0), 0);
const distinct = (rows, pick) => new Set(rows.map(pick).filter((v) => v !== undefined && v !== null)).size;

/** CASA-wise issuance report (default reports tab), at four grains — see
 *  GROUP_BY_OPTIONS. Every grain reflects the CASA, position and size
 *  recorded ON THE ISSUANCE at issue time, not the employee's current
 *  assignment, so history stays accurate after moves. */
const IssuanceReport = ({ itemOptions, itemOptionsLoading }) => {
  const navigate = useNavigate();
  const { costCenter } = useSelector((state) => state.common);

  // Rows and the grain they came back in are one piece of state, as on the
  // lifecycle tab: the columns must describe the rows under them, including
  // while a request for another grain is still in flight.
  const [result, setResult] = useState({ rows: [], group: null, costCenters: [], totals: null, count: 0 });
  const [loading, setLoading] = useState(false);
  const [pageSettings, setPageSettings] = useState({ ...defaultPagination });
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);

  // Guarded with `ignore` so a slow response for filters the user has since
  // changed can't clobber newer results (including the loading-state reset).
  const fetchData = useCallback(() => {
    let ignore = false;
    setLoading(true);
    const params = cleanParams(applied);
    // Captured from the request going out, not read at response time.
    const askedGroup = groupFor(params.group_by);
    // Grouped grains are unpaginated server-side; sending a page would
    // promise a control the response cannot honour.
    if (!askedGroup) {
      params.page = pageSettings.page;
      params.page_size = pageSettings.pageSize;
    }
    getIssuanceReportApi(params).then((response) => {
      if (ignore) return;
      const body = response.data || {};
      setResult({
        rows: (askedGroup ? body.data : body.results) || [],
        group: askedGroup,
        // Only the matrix carries a column list and totals.
        costCenters: body.cost_centers || [],
        totals: body.totals || null,
        count: body.count || 0,
      });
      if (!askedGroup) setPageSettings((prev) => ({ ...prev, total: body.count || 0 }));
    }).catch((e) => {
      if (ignore) return;
      const message = uniformErrorMessage(e, 'Failed to load issuance report');
      toast.error(message, toastOpts);
      // Drop the rows too: the previous grain's table must not sit under the
      // filters now shown in the bar.
      setResult({ rows: [], group: askedGroup, costCenters: [], totals: null, count: 0 });
    }).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [applied, pageSettings.page, pageSettings.pageSize]);

  useEffect(() => fetchData(), [fetchData]);

  const handleChange = (pagination, filters, sorter) => {
    setPageSettings(handlePageChange(pagination, filters, sorter, pageSettings));
  };

  const handleApply = () => {
    setApplied(draft);
    setPageSettings((prev) => ({ ...prev, page: 1 }));
  };

  const handleClear = () => {
    setDraft(DEFAULT_FILTERS);
    setApplied(DEFAULT_FILTERS);
    setPageSettings((prev) => ({ ...prev, page: 1 }));
  };

  /** A roll-up row becomes the next grain down, filtered to that row and
   *  carrying the status the roll-up counted under so the two reconcile.
   *  Written to both draft and applied so the bar shows what it applied. */
  const drillDown = (next) => {
    const filters = { ...applied, status: applied.status || GROUPED_STATUSES, ...next };
    setDraft(filters);
    setApplied(filters);
    setPageSettings((prev) => ({ ...prev, page: 1 }));
  };

  const grain = result.group?.value || null;
  const isMatrix = grain === 'matrix';

  /** What the heading names after the em dash, or null to keep the generic
   *  title. Ids survive the CSV round-trip as strings, hence String() on
   *  both sides. Only ONE cost centre earns a name: with several the table
   *  is no longer "the report for X", and listing them would push the
   *  heading past a line. The count is the honest summary — and the filter
   *  bar spells out which ones directly underneath. */
  const selectedCasaName = useMemo(() => {
    const raw = applied.cost_center;
    if (raw === undefined || raw === null || raw === '') return null;
    const ids = String(raw).split(',').map((s) => s.trim()).filter(Boolean);
    if (ids.length === 0) return null;
    if (ids.length > 1) return `${ids.length} cost centers`;
    const match = (costCenter || []).find((c) => String(c.id) === ids[0]);
    // Falls back to null rather than the raw id: a heading reading
    // "Cost Center-wise issuance — 42" is worse than the generic title while
    // the cost-centre list is still loading.
    return match?.name || match?.code || null;
  }, [applied.cost_center, costCenter]);

  const pinLeft = useStickyIdentity();

  const qty = (v) => <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{v ?? 0}</strong>;

  const costCenterColumns = [
    { title: 'Cost Center', dataIndex: 'cost_center_name', ...pinLeft(200), render: (v) => v || '-' },
    { title: 'Code', dataIndex: 'cost_center_code', render: (v) => v || '-' },
    { title: 'Employees', dataIndex: 'employees', align: 'right' },
    { title: 'Items', dataIndex: 'items', align: 'right' },
    { title: 'Qty Issued', dataIndex: 'quantity', align: 'right', render: qty },
    {
      title: '', key: 'drill', width: 130,
      // The Unassigned bucket has no id to filter by, so it cannot drill.
      render: (_, r) => (r.cost_center_id === null ? null : (
        <button type="button" className="btn btn-link btn-sm p-0" style={{ color: '#D3001A' }}
          onClick={() => drillDown({ group_by: 'item', cost_center: String(r.cost_center_id) })}>
          View items
        </button>
      )),
    },
  ];

  const itemColumns = [
    // Written once per run of rows, and never on a size row. The server
    // orders by cost centre then item, so a run is contiguous; repeating
    // the store on all five of its items — and again on every nested size —
    // turns the roll-up back into the flat list this grain exists to
    // replace. `index` is the position within this level of the tree, so
    // top-level rows index into `result.rows` and children never reach the
    // comparison at all.
    {
      title: 'Cost Center', dataIndex: 'cost_center_name', ...pinLeft(200),
      render: (v, r, index) => {
        if (r.size_id !== undefined) return '';
        const previous = result.rows[index - 1];
        if (previous && previous.cost_center_id === r.cost_center_id) return '';
        return v || '-';
      },
    },
    { title: 'Item', dataIndex: 'uniform_item_name', ...truncatedText(UNIFORM_LANE), render: (v, r) => (r.size_id !== undefined ? '' : v) },
    { title: 'Size', dataIndex: 'size_code', width: 90, render: (v) => v || '' },
    { title: 'Employees', dataIndex: 'employees', align: 'right' },
    { title: 'Qty Issued', dataIndex: 'quantity', align: 'right', render: qty },
    {
      title: '', key: 'drill', width: 150,
      render: (_, r) => (r.size_id !== undefined ? null : (
        <button type="button" className="btn btn-link btn-sm p-0" style={{ color: '#D3001A' }}
          onClick={() => drillDown({
            group_by: undefined,
            cost_center: r.cost_center_id === null ? undefined : String(r.cost_center_id),
            uniform_item: String(r.uniform_item_id),
          })}>
          View issuances
        </button>
      )),
    },
  ];

  const matrixColumns = [
    { title: 'Item', dataIndex: 'uniform_item_name', ...pinLeft(UNIFORM_LANE), ellipsis: { showTitle: true } },
    ...result.costCenters.map((cc) => ({
      // Headed by the store name, as on the lifecycle matrix — codes were
      // tried there and read worse; the header wraps rather than widening.
      title: <span className="issuance-matrix-store">{cc.name}</span>,
      key: `cc-${cc.key}`, align: 'right',
      render: (_, r) => {
        const cell = r.cells?.[cc.key];
        if (!cell) return <span className="text-muted">–</span>;
        // A link, but not dressed as one: blue here read as a second value.
        return (
          <button type="button" className="btn btn-link btn-sm p-0 text-reset"
            style={{ fontVariantNumeric: 'tabular-nums', textDecoration: 'none' }}
            aria-label={`${cc.name} — ${r.uniform_item_name}: view issuances`}
            onClick={() => drillDown({
              group_by: undefined,
              cost_center: cc.id === null ? undefined : String(cc.id),
              uniform_item: String(r.uniform_item_id),
            })}>
            <strong>{cell}</strong>
          </button>
        );
      },
    })),
    { title: 'Total', dataIndex: 'quantity', align: 'right', render: qty, className: TOTAL },
  ];

  /** Grand total under the cost-centre roll-up. Only the garments are
   *  added up: `employees` and `items` are DISTINCT counts per store, and
   *  one person issued at two stores would be counted twice by a sum — a
   *  total that is wrong is worse than a cell left blank. */
  const costCenterSummary = () => {
    if (grain !== 'cost_center' || !result.rows.length) return null;
    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}><strong>All cost centers</strong></Table.Summary.Cell>
          <Table.Summary.Cell index={1} />
          <Table.Summary.Cell index={2} />
          <Table.Summary.Cell index={3} />
          <Table.Summary.Cell index={4} align="right">
            <strong>{sumOf(result.rows, (r) => r.quantity)}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5} />
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  // Column totals under the matrix: one cell per column, in column order.
  const matrixSummary = () => {
    if (!isMatrix || !result.totals || !result.rows.length) return null;
    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}><strong>All items</strong></Table.Summary.Cell>
          {result.costCenters.map((cc, i) => (
            <Table.Summary.Cell index={1 + i} key={`cc-${cc.key}`} align="right">
              <strong>{result.totals.by_cost_center?.[cc.key] ?? 0}</strong>
            </Table.Summary.Cell>
          ))}
          <Table.Summary.Cell index={1 + result.costCenters.length} align="right" className={TOTAL}>
            <strong>{result.totals.quantity ?? 0}</strong>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  const detailColumns = [
    // Reference at column 0 because a fixed-left run must start there:
    // pinning it at index 1 sets rc-table's `hasGapFixed` and antd then
    // forces `box-shadow: none` on the lane, leaving a column that silently
    // overlaps scrolled content with no edge. Reference-first also matches
    // every other list in the module. The Excel export is server-side, so
    // column order is a front-end concern only.
    {
      title: 'Reference', dataIndex: 'batch_reference', ...pinLeft(170),
      render: (ref, record) => ref ? (
        record.batch ? (
          <button type="button" className="btn btn-link p-0" style={{ color: '#D3001A' }}
            onClick={() => navigate(`/uniform-management/issuance/batches/${record.batch}`)}>
            {ref}
          </button>
        ) : ref
      ) : '-'
    },
    { title: 'Date', dataIndex: 'issuance_date', render: (d) => formatDateHandler(d) },
    { title: 'Cost Center', dataIndex: 'casa_name', render: (v) => v || '-' },
    { title: 'Employee ID', dataIndex: 'company_user_emp_no' },
    { title: 'Employee', dataIndex: 'company_user_name' },
    { title: 'Position', dataIndex: 'position_name', render: (v) => v || '-' },
    { title: 'Gender', dataIndex: 'gender', render: (g) => getGenderLabel(g) },
    { title: 'Uniform', dataIndex: 'uniform_item_name', ...truncatedText(UNIFORM_LANE) },
    { title: 'Size', dataIndex: 'size_code', render: (v) => v || '-' },
    { title: 'Qty', dataIndex: 'quantity' },
    { title: 'Warehouse', dataIndex: 'warehouse_name' },
    { title: 'Issued By', dataIndex: 'issued_by_name', render: (v) => v || '-' },
    { title: 'Status', dataIndex: 'status', render: (s) => <span style={getStatusBadge(s)}>{s}</span> },
  ];

  const columns = isMatrix ? matrixColumns
    : grain === 'cost_center' ? costCenterColumns
      : grain === 'item' ? itemColumns : detailColumns;

  const rowKey = grain ? (r) => r.key ?? (r.cost_center_id === null ? 'unassigned' : r.cost_center_id) : (r) => r.id;

  /** Garments, never rows: switching Group by must not change what a number
   *  means. `employees` is deliberately absent — it is a DISTINCT count per
   *  row, and summing it across rows would count one person once per store
   *  or size they appear in. */
  const totalQuantity = isMatrix ? (result.totals?.quantity ?? 0) : sumOf(result.rows, (r) => r.quantity);
  const costCenterCount = isMatrix ? result.costCenters.length
    : grain === 'cost_center' ? result.rows.length
      : grain === 'item' ? distinct(result.rows, (r) => r.cost_center_id)
        : distinct(result.rows, (r) => r.casa_name);
  // Only where it can be counted honestly: a cost-centre row carries its own
  // distinct item count, and those cannot be added up across stores.
  const itemCount = isMatrix ? result.rows.length
    : grain === 'item' ? distinct(result.rows, (r) => r.uniform_item_id)
      : grain === 'cost_center' ? null : distinct(result.rows, (r) => r.uniform_item_name);

  // Two cards share the row evenly rather than leaving a third of it empty.
  const cardSpan = itemCount === null ? 'col-lg-6' : 'col-lg-4';

  const StatCard = ({ label, value, color, icon }) => (
    <div className={`col-sm-6 ${cardSpan} mb-3`}>
      <div className="card mb-0" style={{ borderLeft: `3px solid ${color}` }}>
        <div className="card-body d-flex align-items-center gap-3 py-3">
          <span style={{ width: 34, height: 34, borderRadius: 8, background: `${color}14`, color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className={icon} />
          </span>
          <div>
            <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h5 className="mb-0" style={{ fontWeight: 600 }}>
          {selectedCasaName ? `Cost Center-wise issuance — ${selectedCasaName}` : 'Issuance (Cost Center-wise)'}
        </h5>
        <ExcelExportButton framed apiFn={exportIssuanceReportApi} params={cleanParams(applied)} filename="Uniform Issuance.xlsx" />
      </div>
      <ReportFilterBar
        fields={FIELDS} primaryFields={PRIMARY_FIELDS}
        value={draft} appliedValue={applied} onChange={setDraft} statusOptions={issuanceStatusOptions}
        itemOptions={itemOptions} itemOptionsLoading={itemOptionsLoading}
        groupByOptions={GROUP_BY_OPTIONS} groupByPlaceholder="Detail (one row per issuance)"
        onApply={handleApply} onClear={handleClear}
      />
      <div className="row mt-3">
        <StatCard label="Garments issued" value={totalQuantity} color="#D3001A" icon="fa fa-shirt" />
        <StatCard label="Cost centers" value={costCenterCount} color="#2563EB" icon="fa fa-store" />
        {itemCount !== null && <StatCard label="Items" value={itemCount} color="#059669" icon="fa fa-list" />}
      </div>
      <p className="text-muted" style={{ fontSize: 12 }}>
        {grain
          ? 'Totals count issued and accepted garments — rejected, returned and no-longer-required items are excluded, so a total is what the store actually drew. Grouped by the cost center recorded when the item was issued, not the employee’s current store.'
          : 'Rows show the cost center, position and size recorded when the item was issued — not the employee’s current assignment.'}
      </p>
      <Table
        className="issuance-table"
        size="small"
        columns={columns}
        dataSource={result.rows}
        rowKey={rowKey}
        loading={loading}
        // The item grain nests its sizes: antd renders `sizes` as tree
        // children, so a size expands in place under its item rather than
        // costing a fourth tab or a second request.
        expandable={grain === 'item' ? { childrenColumnName: 'sizes' } : { childrenColumnName: '__none' }}
        summary={isMatrix ? matrixSummary : grain === 'cost_center' ? costCenterSummary : undefined}
        pagination={grain ? false : {
          total: pageSettings.total, pageSize: pageSettings.pageSize, current: pageSettings.page,
          pageSizeOptions, itemRender, showSizeChanger: true
        }}
        onChange={handleChange}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default IssuanceReport;
